import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Shield, Users, PlusCircle, Loader2, Save, X } from 'lucide-react';
import { roleService } from '../services/api';

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newRole, setNewRole] = useState({ name: '', permissions: '' });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsersAndRoles();
  }, []);

  const fetchUsersAndRoles = async () => {
    try {
      setLoading(true);
      const token = user?.token || localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }
  
      console.log('Fetching users and roles...');
      
      // Use Promise.all to fetch both users and roles concurrently
      const [usersData, rolesData] = await Promise.all([
        roleService.getUsers(),
        roleService.getRoles()
      ]);
      
      console.log('Users data:', usersData);
      console.log('Roles data:', rolesData);
  
      // Both responses should have a 'roles' property based on your backend
      setUsers(usersData.roles || []);
      setRoles(rolesData.roles || []);
      setError('');
    } catch (err) {
      console.error('Error fetching users and roles:', err);
      setError('Failed to load users and roles');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await roleService.createRole({
        name: newRole.name,
        permissions: newRole.permissions.split(',').map(p => p.trim())
      });
      
      setNewRole({ name: '', permissions: '' });
      await fetchUsersAndRoles();
    } catch (err) {
      setError('Failed to create role');
      console.error(err);
    }
  };
  
  const handleAssignRole = async (userId, roleId) => {
    try {
      await roleService.assignRole(userId, roleId);
      await fetchUsersAndRoles();
    } catch (err) {
      setError('Failed to assign role');
      console.error(err);
    }
  };
  
  const handleRemoveRole = async (userId, roleId) => {
    try {
      await roleService.removeRole(userId, roleId);
      await fetchUsersAndRoles();
    } catch (err) {
      setError('Failed to remove role');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Create Role Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Shield className="mr-2 h-5 w-5" />
          Create New Role
        </h2>
        <form onSubmit={handleCreateRole} className="flex gap-4">
          <input
            type="text"
            value={newRole.name}
            onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
            placeholder="Role name"
            className="flex-1 p-2 border border-gray-300 rounded"
          />
          <input
            type="text"
            value={newRole.permissions}
            onChange={(e) => setNewRole({ ...newRole, permissions: e.target.value })}
            placeholder="Permissions (comma-separated)"
            className="flex-1 p-2 border border-gray-300 rounded"
          />
          <button
            type="submit"
            disabled={!newRole.name || !newRole.permissions}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            <PlusCircle className="h-5 w-5" />
          </button>
        </form>
      </div>

      {/* Users and Roles Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <Users className="mr-2 h-5 w-5" />
            User Roles
          </h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Current Roles</TableHead>
              <TableHead>Assign Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.user_id}>
                <TableCell>
                  {user.first_name} {user.last_name}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {user.roles?.map((role) => (
                      <span 
                        key={role.role_id}
                        className="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
                      >
                        {role.name}
                        <button
                          onClick={() => handleRemoveRole(user.user_id, role.role_id)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <select
                    onChange={(e) => handleAssignRole(user.user_id, e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                    value=""
                  >
                    <option value="">Add role...</option>
                    {roles.map((role) => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Admin;